import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Save } from "lucide-react";
import { fetchPlatformLanding, updatePlatformLanding } from "@/lib/platform-api";

const SLABS = [
  { id: "nav", label: "Navigation" },
  { id: "hero", label: "Hero" },
  { id: "trust", label: "Trust strip" },
  { id: "problemSolution", label: "Problem → Solution" },
  { id: "platform", label: "Platform grid" },
  { id: "eventTypes", label: "Event types" },
  { id: "whyAivory", label: "Why Aivory" },
  { id: "caseStudies", label: "Case studies" },
  { id: "integrations", label: "Integrations" },
  { id: "pricing", label: "Pricing" },
  { id: "faq", label: "FAQ" },
  { id: "cta", label: "Closing CTA" },
  { id: "footer", label: "Footer" },
];

function Field({ label, value, onChange, multiline, hint }) {
  return (
    <label className="block">
      <span className="text-xs text-[var(--text-tertiary)]">{label}</span>
      {hint ? <span className="block text-[10px] text-[var(--text-tertiary)] mt-0.5">{hint}</span> : null}
      {multiline ? (
        <textarea className="input-dark w-full mt-1 min-h-[80px] text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className="input-dark w-full mt-1 text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

function SlabEditor({ slabId, content, onChange }) {
  const patch = (key, val) => onChange({ ...content, [key]: val });

  if (slabId === "nav") {
    const n = content.nav || {};
    return (
      <div className="space-y-4">
        <Field label="Header button label" value={n.launchLabel} onChange={(v) => onChange({ ...content, nav: { ...n, launchLabel: v } })} />
        <p className="text-xs text-[var(--text-tertiary)]">Nav anchor links are edited in the raw JSON below if you need to add or reorder sections.</p>
      </div>
    );
  }

  if (slabId === "hero") {
    const h = content.hero || {};
    return (
      <div className="space-y-4">
        <Field label="Eyebrow" value={h.eyebrow} onChange={(v) => patch("hero", { ...h, eyebrow: v })} />
        <Field label="Title line 1" value={h.title} onChange={(v) => patch("hero", { ...h, title: v })} />
        <Field label="Title accent (orange)" value={h.titleAccent} onChange={(v) => patch("hero", { ...h, titleAccent: v })} />
        <Field label="Subtitle" value={h.subtitle} onChange={(v) => patch("hero", { ...h, subtitle: v })} multiline />
        <Field label="Primary CTA label" value={h.primaryCta?.label} onChange={(v) => patch("hero", { ...h, primaryCta: { ...h.primaryCta, label: v } })} />
        <Field
          label="Primary CTA href"
          value={h.primaryCta?.href}
          onChange={(v) => patch("hero", { ...h, primaryCta: { ...h.primaryCta, href: v } })}
          hint="Use /signup for self-serve Studio trial"
        />
        <Field label="Secondary CTA label" value={h.secondaryCta?.label} onChange={(v) => patch("hero", { ...h, secondaryCta: { ...h.secondaryCta, label: v } })} />
        <Field
          label="Secondary CTA href"
          value={h.secondaryCta?.href}
          onChange={(v) => patch("hero", { ...h, secondaryCta: { ...h.secondaryCta, href: v } })}
        />
      </div>
    );
  }

  if (slabId === "trust") {
    const t = content.trust || {};
    return (
      <div className="space-y-4">
        <Field label="Label" value={t.label} onChange={(v) => patch("trust", { ...t, label: v })} />
        <Field
          label="Sponsors (comma-separated)"
          value={(t.sponsors || []).join(", ")}
          onChange={(v) => patch("trust", { ...t, sponsors: v.split(",").map((s) => s.trim()).filter(Boolean) })}
        />
      </div>
    );
  }

  if (slabId === "platform") {
    const p = content.platform || {};
    return (
      <div className="space-y-4">
        <Field label="Overline" value={p.overline} onChange={(v) => patch("platform", { ...p, overline: v })} />
        <Field label="Title line 1" value={p.title} onChange={(v) => patch("platform", { ...p, title: v })} />
        <Field label="Title line 2" value={p.titleLine2} onChange={(v) => patch("platform", { ...p, titleLine2: v })} />
        <Field label="Description" value={p.description} onChange={(v) => patch("platform", { ...p, description: v })} multiline />
        <p className="text-xs text-[var(--text-tertiary)]">
          Feature cards ({(p.features || []).length} items) — edit titles in JSON for bulk changes.
        </p>
      </div>
    );
  }

  if (slabId === "problemSolution") {
    const o = content.problemSolution || {};
    return (
      <div className="space-y-4">
        <Field label="Overline" value={o.overline} onChange={(v) => patch("problemSolution", { ...o, overline: v })} />
        <Field label="Title" value={o.title} onChange={(v) => patch("problemSolution", { ...o, title: v })} />
        <Field label="Pain column title" value={o.painTitle} onChange={(v) => patch("problemSolution", { ...o, painTitle: v })} />
        <Field label="Solution column title" value={o.solutionTitle} onChange={(v) => patch("problemSolution", { ...o, solutionTitle: v })} />
        <Field
          label="Pains (one per line)"
          value={(o.pains || []).join("\n")}
          onChange={(v) => patch("problemSolution", { ...o, pains: v.split("\n").map((s) => s.trim()).filter(Boolean) })}
          multiline
        />
        <Field
          label="Solutions (one per line)"
          value={(o.solutions || []).join("\n")}
          onChange={(v) => patch("problemSolution", { ...o, solutions: v.split("\n").map((s) => s.trim()).filter(Boolean) })}
          multiline
        />
      </div>
    );
  }

  if (slabId === "eventTypes") {
    const et = content.eventTypes || {};
    return (
      <div className="space-y-4">
        <Field label="Overline" value={et.overline} onChange={(v) => patch("eventTypes", { ...et, overline: v })} />
        <Field label="Title" value={et.title} onChange={(v) => patch("eventTypes", { ...et, title: v })} />
        <Field
          label="Cards (one per line)"
          value={(et.items || []).join("\n")}
          onChange={(v) => patch("eventTypes", { ...et, items: v.split("\n").map((s) => s.trim()).filter(Boolean) })}
          multiline
        />
      </div>
    );
  }

  if (slabId === "whyAivory") {
    const w = content.whyAivory || {};
    return (
      <div className="space-y-4">
        <Field label="Overline" value={w.overline} onChange={(v) => patch("whyAivory", { ...w, overline: v })} />
        <Field label="Title" value={w.title} onChange={(v) => patch("whyAivory", { ...w, title: v })} />
        <p className="text-xs text-[var(--text-tertiary)]">Differentiator cards are edited in JSON for title/body pairs.</p>
      </div>
    );
  }

  if (slabId === "caseStudies") {
    const cs = content.caseStudies || {};
    return (
      <div className="space-y-4">
        <Field label="Overline" value={cs.overline} onChange={(v) => patch("caseStudies", { ...cs, overline: v })} />
        <Field label="Title" value={cs.title} onChange={(v) => patch("caseStudies", { ...cs, title: v })} />
        <p className="text-xs text-[var(--text-tertiary)]">Case studies use customer/challenge/implementation/result in JSON.</p>
      </div>
    );
  }

  if (slabId === "integrations") {
    const i = content.integrations || {};
    return (
      <div className="space-y-4">
        <Field label="Overline" value={i.overline} onChange={(v) => patch("integrations", { ...i, overline: v })} />
        <Field label="Title" value={i.title} onChange={(v) => patch("integrations", { ...i, title: v })} />
        <Field label="Description" value={i.description} onChange={(v) => patch("integrations", { ...i, description: v })} multiline />
        <Field
          label="Tools (comma-separated)"
          value={(i.tools || []).join(", ")}
          onChange={(v) => patch("integrations", { ...i, tools: v.split(",").map((s) => s.trim()).filter(Boolean) })}
        />
      </div>
    );
  }

  if (slabId === "pricing") {
    const pr = content.pricing || {};
    return (
      <div className="space-y-4">
        <Field label="Overline" value={pr.overline} onChange={(v) => patch("pricing", { ...pr, overline: v })} />
        <Field label="Title" value={pr.title} onChange={(v) => patch("pricing", { ...pr, title: v })} />
        <Field label="Muted suffix" value={pr.titleMuted} onChange={(v) => patch("pricing", { ...pr, titleMuted: v })} />
        <p className="text-xs text-[var(--text-tertiary)]">
          Plan cards — edit prices and CTAs in JSON. Studio trial: set <span className="font-mono">ctaHref</span> to{" "}
          <span className="font-mono">/signup</span>.
        </p>
      </div>
    );
  }

  if (slabId === "faq") {
    const f = content.faq || {};
    return (
      <div className="space-y-4">
        <Field label="Overline" value={f.overline} onChange={(v) => patch("faq", { ...f, overline: v })} />
        <Field label="Title" value={f.title} onChange={(v) => patch("faq", { ...f, title: v })} />
        <p className="text-xs text-[var(--text-tertiary)]">FAQ question/answer items are edited in JSON.</p>
      </div>
    );
  }

  if (slabId === "cta") {
    const c = content.cta || {};
    return (
      <div className="space-y-4">
        <Field label="Title" value={c.title} onChange={(v) => patch("cta", { ...c, title: v })} />
        <Field label="Accent" value={c.titleAccent} onChange={(v) => patch("cta", { ...c, titleAccent: v })} />
        <Field label="Supporting line" value={c.body} onChange={(v) => patch("cta", { ...c, body: v })} multiline />
        <Field label="Button label" value={c.buttonLabel} onChange={(v) => patch("cta", { ...c, buttonLabel: v })} />
        <Field label="Secondary button label" value={c.secondaryLabel} onChange={(v) => patch("cta", { ...c, secondaryLabel: v })} />
      </div>
    );
  }

  if (slabId === "footer") {
    const f = content.footer || {};
    return (
      <div className="space-y-4">
        <Field label="Copyright line" value={f.copyright} onChange={(v) => patch("footer", { ...f, copyright: v })} />
      </div>
    );
  }

  return null;
}

export default function PlatformLanding() {
  const [content, setContent] = useState(null);
  const [activeSlab, setActiveSlab] = useState("hero");
  const [jsonText, setJsonText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPlatformLanding();
      setContent(data.content);
      setJsonText(JSON.stringify(data.content, null, 2));
    } catch (err) {
      toast.error("Could not load landing CMS", { description: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      let payload = content;
      try {
        payload = JSON.parse(jsonText);
      } catch {
        throw new Error("JSON editor has invalid syntax — fix before saving.");
      }
      const data = await updatePlatformLanding(payload);
      setContent(data.content);
      setJsonText(JSON.stringify(data.content, null, 2));
      toast.success("Homepage published", { description: "Changes are live at aivoryems.com" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading || !content) {
    return <p className="p-8 text-sm text-[var(--text-secondary)]">Loading homepage editor…</p>;
  }

  return (
    <div className="p-8 lg:p-12 max-w-[1200px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-overline">Platform</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Marketing homepage</h1>
          <p className="mt-3 text-sm text-[var(--text-secondary)] max-w-xl">
            Edit copy for each section on <strong className="text-[var(--text-primary)]">aivoryems.com</strong>. The public
            page always renders in dark mode and does not change admin or workspace theme settings.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href="https://aivoryems.com" target="_blank" rel="noreferrer" className="btn-secondary text-sm">
            Preview site <ExternalLink size={14} />
          </a>
          <button type="button" className="btn-primary text-sm" onClick={save} disabled={busy}>
            <Save size={14} /> {busy ? "Saving…" : "Publish changes"}
          </button>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-[220px_1fr] gap-6">
        <nav className="flex lg:flex-col gap-1 flex-wrap">
          {SLABS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveSlab(s.id)}
              className={`px-3 py-2 text-sm text-left rounded-sm transition-colors ${
                activeSlab === s.id
                  ? "bg-[var(--bg-elevated)] text-[var(--text-primary)]"
                  : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </nav>
        <div className="surface p-6">
          <h2 className="font-heading text-lg mb-4">{SLABS.find((s) => s.id === activeSlab)?.label}</h2>
          <SlabEditor
            slabId={activeSlab}
            content={content}
            onChange={(next) => {
              setContent(next);
              setJsonText(JSON.stringify(next, null, 2));
            }}
          />
        </div>
      </div>

      <div className="mt-10 surface p-6">
        <h2 className="font-heading text-lg mb-2">Advanced · full JSON</h2>
        <p className="text-xs text-[var(--text-tertiary)] mb-4">
          Edit feature grids, pricing plans, nav anchors, and hero stats. Invalid JSON cannot be saved.
        </p>
        <textarea
          className="input-dark w-full min-h-[320px] font-mono text-xs"
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
        />
      </div>
    </div>
  );
}
